import { memo } from 'react';

import useWerklijnen from '@/apps/mwd-v2/hooks/useWerklijnen';
import type { AcceptConflictDialogData } from '@/apps/mwd-v2/state/types/ActionStore';
import type { MwdWerklijn } from '@/apps/mwd-v2/types/Domain';
import { resolveWerklijnIds } from '@/apps/mwd-v2/utils/resolveWerklijnIds';
import { useHasPermission } from '@/auth';
import useClientState from '@/state/useClientState';
import { Group } from '@visx/group';

import Werklijn from '../Domain/Werklijn';

const WerklijnMemo = memo(Werklijn);

type SelectedWerklijnProps = {
  dialogData?: AcceptConflictDialogData[] | null;
  /**
   * Explicit werklijnIds to render. When provided this takes precedence over
   * dialogData / the selected item, and multiple werklijnen are stacked.
   */
  werklijnIds?: string[];
};

const SelectedWerklijn = ({ dialogData, werklijnIds }: SelectedWerklijnProps) => {
  const selectedItem = useClientState((state) => state.selected.selectedItems);
  const { miniWerklijnen } = useWerklijnen();
  const miniMwdWidth = useClientState((state) => state.miniMwd.width);
  const isAllowedToMutate = useHasPermission('mwd.mutate');

  const resolvedIds = werklijnIds?.length ? werklijnIds : resolveWerklijnIds(dialogData, selectedItem[0]?.werklijnId);

  const werklijnenToRender = resolvedIds
    .map((id) => miniWerklijnen.find((werklijn) => werklijn.werklijnId === id))
    .filter((werklijn): werklijn is MwdWerklijn => Boolean(werklijn));

  if (!werklijnenToRender.length) return null;

  let offsetY = 40;

  return (
    <>
      {werklijnenToRender.map((werklijn) => {
        const werklijnY = offsetY;
        offsetY += werklijn.height;
        return (
          <Group
            key={`werklijn-${werklijn.werklijnId}`}
            top={werklijnY}
            left={0}
            width={miniMwdWidth}
            height={werklijn.height}
          >
            <WerklijnMemo isAllowedToMutate={isAllowedToMutate} werklijn={werklijn} werklijnY={0} miniMwd={true} />
          </Group>
        );
      })}
    </>
  );
};

export default SelectedWerklijn;
